/**
 * Resolve the in-app route for a notification — driven entirely by
 * backend-computed fields, never by client-side inference.
 *
 * `metadata.source_type` and `metadata.selected_tab` are computed once, at
 * write time, by the backend (see backend/app/modules/notifications/routing.py
 * :apply_routing_defaults, called from notifications/service.py
 * :create_notifications) and are present on every notification created going
 * forward. This function does not infer routing from `type`/`category`/which
 * id fields happen to be present — if `source_type` is unmapped, fall back to
 * the backend's `selected_tab` alone; if neither is present (only possible
 * for notifications stored before this field existed), land on the plain
 * notifications list rather than guess.
 *
 * See docs/fcm_notification_data_contract.md and
 * docs/inapp_notification_websocket_contract.md for the full contract.
 */

export interface NotificationLike {
  category?: string;
  type?: string;
  metadata?: Record<string, unknown>;
}

export function getNotificationDeepLink(item: NotificationLike): string {
  const metadata  = item.metadata || {};
  const sourceType = String(metadata.source_type ?? "");
  const selectedTab = String(metadata.selected_tab ?? "");
  const threadId  = String(metadata.thread_id  ?? "");
  const dealId    = String(metadata.deal_id    ?? "");
  const leadId    = String(metadata.lead_id    ?? "");
  const sessionId = String(metadata.session_id ?? "");
  const jobId     = String(metadata.job_id     ?? "");
  const suggestionId = String(metadata.suggestion_id ?? "");
  const geoUserId   = String(metadata.user_id     ?? "");
  const geoUserName = String(metadata.seller_name ?? "");
  // "news.liked" stores news_id (news/service.py:schedule_content_liked),
  // "news.published" stores article_id (feed_push.py:notify_news_feed) —
  // same source_type, two id keys.
  const articleId = String(metadata.article_id ?? metadata.news_id ?? "");
  const webinarId = String(metadata.webinar_id ?? "");

  // Admin-authored override. The broadcast composer stores a free-text
  // `deep_link` per broadcast (admin/broadcast.py:BroadcastRequest.deep_link,
  // stamped into metadata alongside broadcast_id) precisely so the admin can
  // pick the landing page — it is an absolute in-app path, e.g.
  // "/community/posts/<id>" (see AdminPollsPage.tsx). It is authoritative, so
  // it is checked before any source_type mapping below. Anything that isn't an
  // in-app absolute path (external URL, empty, a stray route key) is ignored
  // rather than handed to the router.
  const deepLinkOverride = String(metadata.deep_link ?? "").trim();
  if (deepLinkOverride.startsWith("/") && !deepLinkOverride.startsWith("//")) {
    return deepLinkOverride;
  }

  switch (sourceType) {
    case "geo_alias_unresolved":
      // Admin-only notification — send them straight to the seller's Market
      // Map so they can see the destination that failed to resolve and add
      // the missing alias (see backend/app/modules/deals/typed/service.py
      // :_notify_admins_unresolved_geo_alias).
      return geoUserId
        ? `/admin/users?market_user=${encodeURIComponent(geoUserId)}${
            geoUserName ? `&market_user_name=${encodeURIComponent(geoUserName)}` : ""
          }`
        : "/admin/users";
    case "deal_create":
    case "deal_edit":
    case "deal_analyzed":
    case "pricing_nudge":
      return dealId ? `/inventory/my?deal_id=${encodeURIComponent(dealId)}` : "/inventory/my";
    case "deal_analysis":
      if (!dealId) return "/inventory/my";
      return jobId
        ? `/inventory/my?deal_id=${encodeURIComponent(dealId)}&job_id=${encodeURIComponent(jobId)}`
        : `/inventory/my?deal_id=${encodeURIComponent(dealId)}`;
    case "recommendation_deal":
      // Buyer-facing deal match — open the marketplace deal detail directly.
      return dealId ? `/?deal_id=${encodeURIComponent(dealId)}` : "/";
    case "lead_alert":
    case "enquiry":
      return leadId
        ? `/enquiries?lead_id=${encodeURIComponent(leadId)}${dealId ? `&deal_id=${encodeURIComponent(dealId)}` : ""}`
        : "/enquiries";
    case "deal_suggestion":
      // No dedicated seller suggestions page yet — surface it in inventory.
      return suggestionId ? `/inventory/my?suggestion_id=${encodeURIComponent(suggestionId)}` : "/inventory/my";
    case "flyer":
    case "video":
    case "itinerary":
      return sessionId ? `/chat?session=${encodeURIComponent(sessionId)}` : "/chat";
    case "dm":
      return threadId ? `/dm/${threadId}` : "/dm";
    case "security":
      // security.login_other_device — this app has no standalone settings
      // route; the password-change control lives on ProfilePage itself
      // (ChangePasswordSection).
      return "/profile";
    case "deal":
      return dealId ? `/inventory/my?deal_id=${encodeURIComponent(dealId)}` : "/inventory/my";
    case "boost_deal":
      // Boost/featured-deal promo (boost/service.py:_fire_inapp_notifications)
      // goes to buyers AND sellers in the campaign audience, so it opens the
      // public marketplace deal — never the recipient's own inventory.
      return dealId ? `/?deal_id=${encodeURIComponent(dealId)}` : "/";
    case "processing":
      // document.processed / processing.deals_extracted — the upload finished
      // being OCR'd/extracted, results land in inventory.
      return "/inventory/my";
    case "profile":
    case "preferences":
    case "verification":
    case "seller_market":
      // profile.*, preferences.divergence_nudge, verification_success and
      // seller_market_* are all edited on the one profile page in this app —
      // it has no per-section route to target.
      return "/profile";
    case "community":
      // community.post_created — the author's post is now live in the feed.
      // This app has no per-post route, only the feed.
      return "/community";
    case "news":
      // No article detail route in this app — WebinarsNewsPage's "News" tab is
      // the only surface. The id rides along for when one exists.
      return articleId
        ? `/webinars-news?tab=news&article_id=${encodeURIComponent(articleId)}`
        : "/webinars-news?tab=news";
    case "webinar":
      return webinarId
        ? `/webinars-news?webinar_id=${encodeURIComponent(webinarId)}`
        : "/webinars-news";
    case "social":
      return "/notifications";
  }

  // source_type unmapped or absent (older notification predating this field) —
  // use the backend's selected_tab if it set one, else the plain list.
  const TAB_TO_PATH: Record<string, string> = {
    inventory: "/inventory/my",
    chat: "/chat",
    leads: "/enquiries",
    notifications: "/notifications",
    deals: "/",
    community: "/community",
    profile: "/profile",
    preferences: "/profile",
    settings: "/profile",
    // No `analytics` entry: this app has no seller analytics page
    // (/admin/analytics is admin-only), so analytics milestones fall through
    // to the bell list here — web-frontend routes them to /analytics.
  };
  return TAB_TO_PATH[selectedTab] ?? "/notifications";
}
